/**
 * NotificationsCenter — the dashboard's notification control panel.
 * ─────────────────────────────────────────────────────────────────────────────
 * Replaces the old single "Title + Message → Send" form with a tabbed centre:
 *   • This device  — grant permission + subscribe THIS browser to Web Push, test it
 *   • Send push    — broadcast to the mobile app (Expo) and/or dashboard operators
 *   • Alert settings (staff) — per-country sound / Web Push / quiet-hours config
 *   • Devices (staff)        — every browser subscribed for this market
 *   • History (staff)        — audit log of alerts the platform sent
 *
 * Country scoping is automatic: every API call carries the active `x-country`
 * header (see api/index.js), and the backend scopes reads/writes to that market.
 * ───────────────────────────────────────────────────────────────────────────── */

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge, Button, Card, Col, Container, Form, Row, Spinner, Tab, Table, Tabs,
} from "react-bootstrap";
import notificationSound from "../../assets/img/newOrderNotification.mp3";
import { toast } from "react-toastify";
import {
  apiSendExpoBroadcast,
  apiBroadcastWebPush,
  apiSendTestWebPush,
  apiListPushDevices,
  apiGetNotificationSettings,
  apiUpdateNotificationSettings,
  apiGetNotificationHistory,
  apiUploadTone,
  apiGetMyNotificationPrefs,
  apiUpdateMyNotificationPrefs,
  apiResetMyNotificationPrefs,
  apiListPushCampaigns,
  apiCancelPushCampaign,
  apiGetPushHealth,
} from "../../api/index";
import { TONES, resolveToneUrl } from "../../utilities/tones";
import {
  isPushSupported,
  getPermission,
  subscribeToWebPush,
  unsubscribeFromWebPush,
  getSubscriptionState,
  announceWebPushState,
} from "../../utilities/webPush";
import { getUserFromLocalStorage } from "../../utilities/user.js";
import { getActiveCountry } from "../../utilities/activeCountry.js";
import {
  PUSH_AUDIENCES,
  PUSH_DEEP_LINKS,
  PUSH_PRODUCT_SOURCES,
  PRODUCT_DEEP_LINK,
  pushMarketOptions,
  validatePushBroadcast,
  buildPushBroadcastBody,
  summarisePushSend,
  summariseWebPushBroadcast,
  PUSH_TIMING,
  toLocalDateTimeInput,
  localTimeZoneLabel,
  pushCampaignStatus,
  isCancellableCampaign,
  campaignAudienceLabel,
  campaignMarketLabel,
  campaignWhen,
  campaignCounts,
  campaignDelivery,
  campaignSender,
  formatLocalDateTime,
} from "../../utilities/pushBroadcast.js";
import ConfirmDialog, { useConfirmDialog } from "../../components/ConfirmDialog.js";
import {
  announceOrderAlertsConfigChanged,
  writeOrderAlertsEnabled,
} from "../../utilities/notificationState.js";
import "./NotificationsCenter.css";
import AppSelect from "../../components/ui/AppSelect";
import useOrderAlerts from "../../hooks/useOrderAlerts";
import SettingHelp from "../Settings/settingsHelp";
import { ReadOnlyNotice } from "../Settings/settingsUi";

// Mirror of the backend isStaffUser() predicate (services/identity/
// webPushAudience.js) for showing staff-only tabs. It used to accept a bare
// user_role of SUPERUSER/SUPERVISOR/ADMIN, which every vendor owner carries —
// so vendors were shown the team-wide Alert settings, Devices, History and
// Send-push tabs. Staff-ness is the staff userType and nothing else; keep this
// in step with the backend or the tabs and the API disagree.
function useIsStaff() {
  const u = getUserFromLocalStorage()?.result || {};
  return u.userType === "LUSUKU STAFF";
}

// ── Shared UI ─────────────────────────────────────────────────────────────────
// Every explanation on this page is a SettingHelp topic from
// views/Settings/help/notificationsHelp.js, the dashboard's one settings
// tooltip, so the copy is reviewed as a set and says plainly which switches
// are saved but not read by anything yet (1 Nov 2026).

// Custom toggle — the Bootstrap-4 dashboard theme doesn't render Bootstrap-5
// `Form.Check type="switch"` visibly, so we draw our own so it's always on-brand.
const Toggle = ({ checked, onChange, id, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={!!checked}
    id={id}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={`nc-toggle${checked ? " on" : ""}`}
  >
    <span className="nc-toggle-thumb" />
  </button>
);

// A labelled setting row: text (+ optional help topic and sub-line) on the
// left, control on the right.
const SettingRow = ({ label, topic, sub, children }) => (
  <div className="nc-setting">
    <div className="nc-setting-text">
      <span className="nc-setting-label">
        {label}
        {topic && <SettingHelp topic={topic} />}
      </span>
      {sub && <span className="nc-setting-sub">{sub}</span>}
    </div>
    <div className="nc-setting-control">{children}</div>
  </div>
);

// A clearly-visible, on-brand loading state (replaces the faint default spinner).
const Loader = ({ label = "Loading…" }) => (
  <div className="nc-loader">
    <Spinner animation="border" role="status" />
    <span>{label}</span>
  </div>
);

// ── Tab 1: This device ──────────────────────────────────────────────────────────
function ThisDeviceTab() {
  const isStaff = useIsStaff();
  const [state, setState] = useState({
    supported: isPushSupported(),
    permission: getPermission(),
    subscribed: false,
    configured: null,
    enabled: null,
    serverReachable: true,
    staleSubscription: false,
  });
  const [busy, setBusy] = useState(false);
  const alerts = useOrderAlerts({ soundUrl: notificationSound });
  const [tonePreview, setTonePreview] = useState({
    name: "Classic",
    url: notificationSound,
    volume: 0.6,
  });

  const refresh = useCallback(async () => {
    const nextState = await getSubscriptionState();
    setState(nextState);
    announceWebPushState(nextState);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    apiGetMyNotificationPrefs()
      .then(({ data }) => {
        const sound = data?.effective?.sound || {};
        const toneId = sound.tone || "classic";
        const toneName =
          toneId === "custom"
            ? data?.prefs?.customTone?.name || "Custom"
            : TONES.find((tone) => tone.id === toneId)?.name || "Classic";
        setTonePreview({
          name: toneName,
          url: resolveToneUrl(
            toneId,
            sound.customToneUrl || data?.prefs?.customTone?.url,
            notificationSound
          ),
          volume:
            typeof sound.volume === "number"
              ? Math.max(0, Math.min(1, sound.volume))
              : 0.6,
        });
      })
      .catch(() => {
        /* The bundled default remains a safe preview fallback. */
      });
  }, []);

  const playEnableTone = () => {
    try {
      const audio = new Audio(tonePreview.url);
      audio.volume = tonePreview.volume;
      audio.play().catch(() => {
        toast.warn("Alerts were enabled, but the browser blocked the tone preview.");
      });
    } catch (_error) {
      toast.warn("Alerts were enabled, but the tone preview could not be played.");
    }
  };

  const enable = async () => {
    setBusy(true);
    // Sound works even when enrolling this browser for push fails.
    alerts.enable({ silent: true, requestPush: false });
    playEnableTone();
    let r;
    try {
      r = await subscribeToWebPush();
    } catch (_error) {
      r = { ok: false, reason: "subscribe_failed" };
    } finally {
      setBusy(false);
    }
    if (r.ok) {
      toast.success(
        `Alerts enabled. ${tonePreview.name} is set to ${Math.round(
          tonePreview.volume * 100
        )}% volume.`
      );
    }
    else if (r.reason === "denied") toast.warn("Sound is on. Browser notifications are blocked; allow them in your browser's site settings.");
    else if (r.reason === "not_configured") toast.warn("Sound is on. Web Push still needs server setup.");
    else if (r.reason === "server_unreachable") toast.warn("Sound is on. The notification server could not be reached.");
    else if (r.reason === "unsupported") toast.warn("Sound is on. This browser doesn't support Web Push.");
    else toast.warn("Sound is on. Couldn't enable Web Push on this device.");
    refresh();
  };

  const toggleSound = () => {
    if (alerts.enabled) alerts.disable({ unsubscribePush: false });
    else {
      alerts.enable({ silent: true, requestPush: false });
      playEnableTone();
    }
  };

  const disable = async () => {
    setBusy(true);
    await unsubscribeFromWebPush();
    writeOrderAlertsEnabled(false);
    setBusy(false);
    toast.info("This device will no longer receive order alerts.");
    refresh();
  };

  const test = async () => {
    setBusy(true);
    try {
      const { data } = await apiSendTestWebPush();
      if (data?.delivered > 0) toast.success(`Test sent to ${data.delivered} device(s).`);
      else toast.warn("Nothing was delivered. Is this browser subscribed?");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Test failed.");
    }
    setBusy(false);
  };

  const permBadge =
    state.permission === "granted" ? <span className="nc-state nc-state--green">Granted</span> :
    state.permission === "denied" ? <span className="nc-state nc-state--red">Blocked</span> :
    <span className="nc-state nc-state--muted">Not asked</span>;

  return (
    <Card className="mt-3 nc-device-card">
      <Card.Body>
        <div className="nc-card-head">
          <span className="nc-chip"><i className="fas fa-bell" /></span>
          <div>
            <h5 className="nc-card-title">Alerts on this browser</h5>
            <p className="nc-card-sub">
              Get a system notification for every new order, even when this tab is
              closed or the browser is minimised. It is set per browser, so turn it on for
              each machine you use. It arrives only while Browser push is on in the team
              alert settings.
            </p>
          </div>
        </div>

        <div className="nc-status-row">
          <div className="nc-status">
            <span className="nc-status-icon"><i className="fas fa-volume-up" /></span>
            <div>
              <span className="nc-status-label">Order alert sound</span>
              <span className={`nc-state nc-state--${alerts.enabled ? "green" : "muted"}`}>
                {alerts.enabled ? "On" : "Off"}
              </span>
            </div>
          </div>
          <Button variant={alerts.enabled ? "outline-danger" : "primary"} onClick={toggleSound}>
            {alerts.enabled ? "Disable order alert sound" : "Enable order alert sound"}
          </Button>
        </div>
        <p className="nc-card-sub">Sound alerts work while the dashboard is open. Browser push is configured separately below.</p>

        {!state.serverReachable && (
          <div className="nc-readiness-callout nc-readiness-callout--danger">
            <i className="fas fa-server" />
            <div>
              <strong>Notification server unavailable</strong>
              <span>Check the API connection, then refresh this page.</span>
            </div>
          </div>
        )}

        {state.serverReachable && state.configured === false && (
          <div className="nc-readiness-callout nc-readiness-callout--warning">
            <i className="fas fa-key" />
            <div>
              <strong>Server setup required</strong>
              <span>
                Web Push is installed but has no VAPID keypair. Device enrollment
                will remain unavailable until the server is configured.
              </span>
              {isStaff && (
                <div className="nc-setup-steps">
                  <code>npx web-push generate-vapid-keys</code>
                  <span>
                    Set <b>VAPID_PUBLIC_KEY</b>, <b>VAPID_PRIVATE_KEY</b>,{" "}
                    <b>VAPID_SUBJECT</b>, and <b>WEB_PUSH_ENABLED=true</b>, then
                    restart the server.
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {state.configured && state.enabled === false && (
          <div className="nc-readiness-callout nc-readiness-callout--info">
            <i className="fas fa-pause-circle" />
            <div>
              <strong>Automatic Web Push is paused</strong>
              <span>
                Set <b>WEB_PUSH_ENABLED=true</b> and restart the server to deliver
                automatic order alerts.
              </span>
            </div>
          </div>
        )}

        {state.staleSubscription && (
          <div className="nc-readiness-callout nc-readiness-callout--info">
            <i className="fas fa-sync-alt" />
            <div>
              <strong>This device needs to be re-enrolled</strong>
              <span>
                The server key changed since this browser subscribed. Press Enable
                below to replace the stale subscription automatically.
              </span>
            </div>
          </div>
        )}

        {!state.supported ? (
          <div className="nc-readiness-callout nc-readiness-callout--warning">
            <i className="fas fa-exclamation-triangle" />
            <div>
              <strong>Web Push is unavailable in this browser</strong>
              <span>Try a current version of Chrome, Edge, or Firefox.</span>
            </div>
          </div>
        ) : (
          <>
            <div className="nc-status-row">
              <div className="nc-status">
                <span className="nc-status-icon"><i className="fas fa-lock" /></span>
                <div>
                  <span className="nc-status-label">Permission</span>
                  {permBadge}
                </div>
              </div>
              <div className="nc-status">
                <span className="nc-status-icon"><i className="fas fa-desktop" /></span>
                <div>
                  <span className="nc-status-label">This device</span>
                  {state.subscribed
                    ? <span className="nc-state nc-state--green">Subscribed</span>
                    : <span className="nc-state nc-state--muted">Not subscribed</span>}
                </div>
              </div>
              <div className="nc-status">
                <span className="nc-status-icon"><i className="fas fa-server" /></span>
                <div>
                  <span className="nc-status-label">Server</span>
                  {state.configured
                    ? <span className="nc-state nc-state--green">Configured</span>
                    : <span className="nc-state nc-state--gold">Setup required</span>}
                </div>
              </div>
              <div className="nc-status">
                <span className="nc-status-icon"><i className="fas fa-broadcast-tower" /></span>
                <div>
                  <span className="nc-status-label">Delivery</span>
                  {state.enabled
                    ? <span className="nc-state nc-state--green">Enabled</span>
                    : <span className="nc-state nc-state--muted">Paused</span>}
                </div>
              </div>
            </div>
            <div className="nc-actions">
              {!state.subscribed ? (
                <Button
                  variant="primary"
                  disabled={busy || !state.configured || !state.serverReachable}
                  onClick={enable}
                >
                  {busy ? <Spinner size="sm" animation="border" /> : <><i className="fas fa-bell" />Enable on this device</>}
                </Button>
              ) : (
                <Button variant="outline-danger" disabled={busy} onClick={disable}>
                  <i className="fas fa-bell-slash" />Disable on this device
                </Button>
              )}
              <Button variant="outline-secondary" disabled={busy || !state.subscribed} onClick={test}>
                <i className="fas fa-paper-plane" />Send test
              </Button>
            </div>
            <div className="nc-tone-summary">
              <i className="fas fa-volume-up" />
              <span>
                Enable preview: <strong>{tonePreview.name}</strong> at{" "}
                <strong>{Math.round(tonePreview.volume * 100)}%</strong> volume
              </span>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
}

// ── Tab: My alerts (personal — visible to EVERY operator, incl. vendors) ─────────
// A vendor's dedicated place to pick their OWN tone / volume / quiet-hours / repeat.
// Overrides the team default just for them; "Reset" re-inherits the team setting.
function MyAlertsTab() {
  const [form, setForm] = useState(null);
  const [hasOverrides, setHasOverrides] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const previewRef = useRef(null);
  const previewTimer = useRef(null);
  const fileRef = useRef(null);

  const hydrate = (data) => {
    const s = data?.effective?.sound || {};
    const q = data?.effective?.quietHours || { enabled: false };
    setForm({
      tone: s.tone || "classic",
      customTone: {
        url: s.customToneUrl || data?.prefs?.customTone?.url || null,
        name: data?.prefs?.customTone?.name || (s.customToneUrl ? "Custom" : null),
      },
      volume: typeof s.volume === "number" ? s.volume : 0.6,
      repeatUntilAck: s.repeatUntilAck === true,
      repeatIntervalMs: s.repeatIntervalMs ?? 8000,
      maxRepeats: s.maxRepeats ?? 8,
      quietHours: {
        enabled: q.enabled === true,
        start: q.start || "22:00",
        end: q.end || "06:00",
        muteSoundOnly: q.muteSoundOnly !== false,
      },
    });
    setHasOverrides(!!data?.prefs);
  };

  useEffect(() => {
    apiGetMyNotificationPrefs()
      .then(({ data }) => hydrate(data))
      .catch((e) => toast.error(e?.response?.data?.error || "Failed to load your preferences"))
      .finally(() => setLoading(false));
  }, []);

  const patch = (path, value) => {
    setForm((f) => {
      const next = JSON.parse(JSON.stringify(f));
      const parts = path.split(".");
      let node = next;
      for (let i = 0; i < parts.length - 1; i += 1) node = node[parts[i]] = node[parts[i]] || {};
      node[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const selectedToneUrl = () => resolveToneUrl(form?.tone, form?.customTone?.url, notificationSound);
  const previewTone = (url, v) => {
    try {
      if (!previewRef.current) previewRef.current = new Audio();
      const a = previewRef.current;
      a.src = url;
      a.currentTime = 0;
      a.volume = Math.max(0, Math.min(1, typeof v === "number" ? v : (form?.volume ?? 0.6)));
      a.play().catch(() => {});
    } catch (_e) { /* ignore */ }
  };
  const onVolumeChange = (v) => {
    patch("volume", v);
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => previewTone(selectedToneUrl(), v), 200);
  };
  const onUploadTone = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("audio/")) { toast.error("Please choose an audio file."); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error("Tone is too large (max ~3MB)."); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setUploading(true);
      try {
        const { data } = await apiUploadTone(reader.result, file.name);
        if (data?.url) {
          patch("customTone", { url: data.url, name: file.name });
          patch("tone", "custom");
          toast.success("Custom tone ready. Press Save my preferences to use it.");
        } else {
          toast.error(data?.error || "Upload failed.");
        }
      } catch (err) {
        toast.error(err?.response?.data?.error || "Upload failed.");
      }
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiUpdateMyNotificationPrefs(form);
      setHasOverrides(true);
      toast.success("Your alert preferences are saved.");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Save failed.");
    }
    setSaving(false);
  };
  const resetToTeam = async () => {
    setSaving(true);
    try {
      await apiResetMyNotificationPrefs();
      const { data } = await apiGetMyNotificationPrefs();
      hydrate(data);
      toast.info("Reset. You now follow the team settings for this market.");
    } catch (e) {
      toast.error("Reset failed.");
    }
    setSaving(false);
  };

  if (loading) return <Loader label="Loading your preferences…" />;
  if (!form) return <div className="alert alert-danger mt-3">Couldn't load your preferences.</div>;
  const vol = form.volume ?? 0.6;

  return (
    <Card className="mt-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: 10 }}>
          <div>
            <h5 className="mb-1"><i className="fas fa-user-cog" />My alert preferences<SettingHelp topic="myAlertPreferences" /></h5>
            <p className="nc-card-sub" style={{ marginBottom: 6 }}>
              Saved to your account and used in place of the team settings, for you only. Saving keeps every value on this tab as yours until you reset.{" "}
              {hasOverrides ? <Badge bg="info">using your own</Badge> : <Badge bg="secondary">using team default</Badge>}
            </p>
          </div>
          {hasOverrides && (
            <Button size="sm" variant="outline-secondary" onClick={resetToTeam} disabled={saving}>
              <i className="fas fa-undo" />Reset to team default
            </Button>
          )}
        </div>

        <Row className="mt-2">
          <Col md={6}>
            <div className="nc-volume">
              <div className="nc-volume-head">
                <span className="nc-setting-label">My alert volume: {Math.round(vol * 100)}%
                  <SettingHelp topic="myAlertVolume" />
                </span>
                <Button size="sm" variant="outline-secondary" onClick={() => previewTone(selectedToneUrl(), vol)}>
                  <i className="fas fa-volume-up" />Preview
                </Button>
              </div>
              <Form.Range min={0} max={1} step={0.05} value={vol}
                onChange={(e) => onVolumeChange(parseFloat(e.target.value))} />
            </div>

            <SettingRow label="Repeat my new order sound" topic="myAlertRepeat" sub="Plays again on a timer until the play limit is reached.">
              <Toggle id="my-repeat" checked={form.repeatUntilAck}
                onChange={(v) => patch("repeatUntilAck", v)} />
            </SettingRow>
            {form.repeatUntilAck && (
              <Row className="mt-2">
                <Col xs={6}>
                  <Form.Label style={{ fontSize: 13 }}>Seconds between plays<SettingHelp topic="operatorAlertRepeatInterval" /></Form.Label>
                  <Form.Control type="number" min={2} value={Math.round((form.repeatIntervalMs ?? 8000) / 1000)}
                    onChange={(e) => patch("repeatIntervalMs", Math.max(2, +e.target.value) * 1000)} />
                </Col>
                <Col xs={6}>
                  <Form.Label style={{ fontSize: 13 }}>Plays in total<SettingHelp topic="operatorAlertMaxPlays" /></Form.Label>
                  <Form.Control type="number" min={1} value={form.maxRepeats ?? 8}
                    onChange={(e) => patch("maxRepeats", Math.max(1, +e.target.value))} />
                </Col>
              </Row>
            )}

            <div className="nc-section-label" style={{ marginTop: 18 }}>
              My tone
              <SettingHelp topic="myAlertTone" />
            </div>
            <div className="nc-tones">
              {TONES.map((t) => (
                <label key={t.id} className={`nc-tone${form.tone === t.id ? " sel" : ""}`}>
                  <input type="radio" name="mytone" checked={form.tone === t.id}
                    onChange={() => patch("tone", t.id)} />
                  <span className="nc-tone-name">{t.name}</span>
                  <button type="button" className="nc-tone-play" aria-label={`Preview ${t.name}`}
                    onClick={(e) => { e.preventDefault(); previewTone(t.url, vol); }}>
                    <i className="fas fa-play" />
                  </button>
                </label>
              ))}
              <label className={`nc-tone${form.tone === "custom" ? " sel" : ""}${form.customTone?.url ? "" : " disabled"}`}>
                <input type="radio" name="mytone" checked={form.tone === "custom"}
                  disabled={!form.customTone?.url}
                  onChange={() => form.customTone?.url && patch("tone", "custom")} />
                <span className="nc-tone-name">{form.customTone?.name || "Custom"}</span>
                {form.customTone?.url && (
                  <button type="button" className="nc-tone-play" aria-label="Preview custom tone"
                    onClick={(e) => { e.preventDefault(); previewTone(form.customTone.url, vol); }}>
                    <i className="fas fa-play" />
                  </button>
                )}
              </label>
            </div>
            <div className="nc-tone-upload">
              <input ref={fileRef} type="file" accept="audio/*" style={{ display: "none" }} onChange={onUploadTone} />
              <Button size="sm" variant="outline-secondary" disabled={uploading} onClick={() => fileRef.current && fileRef.current.click()}>
                {uploading ? <Spinner size="sm" animation="border" /> : <><i className="fas fa-upload" />Upload your own</>}
              </Button>
              <span className="nc-tone-hint">MP3 or WAV, up to ~3MB.</span>
            </div>
          </Col>

          <Col md={6}>
            <div className="nc-section-label">My quiet hours</div>
            <SettingRow label="Use my quiet hours" topic="myAlertQuietHours" sub="Saved, but nothing applies quiet hours yet.">
              <Toggle id="my-quiet" checked={form.quietHours.enabled}
                onChange={(v) => patch("quietHours.enabled", v)} />
            </SettingRow>
            {form.quietHours.enabled && (
              <>
                <Row className="mt-1">
                  <Col xs={6}>
                    <Form.Label style={{ fontSize: 13 }}>From</Form.Label>
                    <Form.Control type="time" value={form.quietHours.start}
                      onChange={(e) => patch("quietHours.start", e.target.value)} />
                  </Col>
                  <Col xs={6}>
                    <Form.Label style={{ fontSize: 13 }}>To</Form.Label>
                    <Form.Control type="time" value={form.quietHours.end}
                      onChange={(e) => patch("quietHours.end", e.target.value)} />
                  </Col>
                </Row>
                <SettingRow label="Mute sound only" topic="operatorAlertMuteSoundOnly" sub="Saved, but nothing applies it yet.">
                  <Toggle id="my-quiet-mute" checked={form.quietHours.muteSoundOnly}
                    onChange={(v) => patch("quietHours.muteSoundOnly", v)} />
                </SettingRow>
              </>
            )}
          </Col>
        </Row>

        <div className="mt-4">
          <Button variant="primary" onClick={save} disabled={saving}>
            {saving ? <Spinner size="sm" animation="border" /> : "Save my preferences"}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}

// A failure always says why. 401/403 is the one an operator can act on
// themselves, so it gets its own wording.
const sendErrorText = (err) => {
  const status = err?.response?.status;
  if (status === 401 || status === 403) return "you need admin rights to send this.";
  return err?.response?.data?.error || err?.message || "the request failed.";
};

// A refused cancel. 409 is not a transport failure: it is the server saying the
// campaign already started or finished, and that sentence is the one the
// operator needs, so it is shown rather than swallowed.
const cancelErrorText = (err) => {
  const status = err?.response?.status;
  const reason = err?.response?.data?.error;
  if (status === 409) {
    return reason || "That campaign has already started, so it cannot be cancelled.";
  }
  if (status === 401 || status === 403) return "You need admin rights to cancel a campaign.";
  return reason || err?.message || "That campaign could not be cancelled.";
};

// ── Push health (admin) ─────────────────────────────────────────────────────────
// The push doctor, on the page where Send is pressed. "Sent to 3 of 3" means
// Expo accepted three messages; it says nothing about whether any phone showed
// one, nor how many customers were never in the audience because their phone
// holds no token. Both answers used to need the Heroku CLI. This card reads
// GET /expoPushTokens/health, the same numbers scripts/pushDoctor.js prints.
const SEVERITY_CALLOUT = { critical: "danger", warning: "warning", info: "info" };
const SEVERITY_ICON = {
  critical: "fas fa-exclamation-triangle",
  warning: "fas fa-exclamation-circle",
  info: "fas fa-info-circle",
};

function PushHealthCard({ onReceiptsChecked }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (params = {}) => {
    try {
      const { data } = await apiGetPushHealth(params);
      setHealth(data?.data || null);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not read push health.");
    }
  }, []);

  useEffect(() => {
    (async () => {
      await load();
      setLoading(false);
    })();
  }, [load]);

  // Drain due receipts now instead of waiting for the five-minute poller, then
  // let the campaign table re-read so its Delivered column catches up.
  const checkNow = async () => {
    setChecking(true);
    await load({ receipts: 1 });
    setChecking(false);
    if (onReceiptsChecked) onReceiptsChecked();
  };

  const platforms = (health?.byPlatform || [])
    .map((p) => `${p.platform === "ios" ? "iOS" : p.platform === "android" ? "Android" : "unknown"} ${p.count}`)
    .join(", ");
  const markets = (health?.byCountry || []).map((c) => `${c.country} ${c.count}`).join(", ");
  const reachable = health?.usersWithTokens || 0;
  const total = health?.totalUsers || 0;
  const reachTone = reachable === 0 ? "red" : total && reachable / total < 0.05 ? "gold" : "green";
  const receiptsTone = (health?.pendingTickets || 0) > 0 && (health?.oldestPendingMinutes || 0) > 60 ? "gold" : "muted";
  const findings = health?.health?.findings || [];
  const errorCodes = health?.errorCodes || [];
  const drained = health?.receipts;

  return (
    <Card className="mt-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0"><i className="fas fa-stethoscope" />Can a push reach anyone?</h5>
          <Button variant="outline-secondary" size="sm" onClick={checkNow} disabled={checking || loading}>
            {checking ? <Spinner size="sm" animation="border" /> : "Check delivery now"}
          </Button>
        </div>
        <p className="nc-card-sub mb-3">
          A send only reaches phones that registered a push token while signed in. "Sent" is what
          Expo accepted; a delivery receipt, about fifteen minutes later, is what a phone actually
          got. Both are read here from what Expo told the server.
        </p>

        {loading ? (
          <Loader label="Reading push health..." />
        ) : error ? (
          <div className="nc-readiness-callout nc-readiness-callout--danger">
            <i className="fas fa-exclamation-triangle" />
            <div><span>{error}</span></div>
          </div>
        ) : (
          <>
            <div className="nc-status-row">
              <div className="nc-status">
                <span className="nc-status-icon"><i className="fas fa-mobile-alt" /></span>
                <div>
                  <span className="nc-status-label">Customers reachable</span>
                  <span className={`nc-state nc-state--${reachTone}`}>{reachable} of {total} accounts</span>
                  <span className="text-muted" style={{ fontSize: 9 }}>{platforms || "no platforms"}{markets ? ` / ${markets}` : ""}</span>
                </div>
              </div>
              <div className="nc-status">
                <span className="nc-status-icon"><i className="fas fa-motorcycle" /></span>
                <div>
                  <span className="nc-status-label">Riders reachable</span>
                  <span className={`nc-state nc-state--${health?.ridersWithTokens ? "green" : "muted"}`}>{health?.ridersWithTokens || 0} riders</span>
                </div>
              </div>
              <div className="nc-status">
                <span className="nc-status-icon"><i className="fas fa-bell-slash" /></span>
                <div>
                  <span className="nc-status-label">Opted out</span>
                  <span className="nc-state nc-state--muted">offers {health?.optedOutOfOffers || 0}, order updates {health?.optedOutOfOrders || 0}</span>
                  <span className="text-muted" style={{ fontSize: 9 }}>A service message still reaches them</span>
                </div>
              </div>
              <div className="nc-status">
                <span className="nc-status-icon"><i className="fas fa-receipt" /></span>
                <div>
                  <span className="nc-status-label">Delivery receipts</span>
                  <span className={`nc-state nc-state--${receiptsTone}`}>
                    {health?.pendingTickets ? `${health.pendingTickets} waiting` : "none waiting"}
                    {health?.pendingTickets && health?.oldestPendingMinutes ? ` (oldest ${Math.round(health.oldestPendingMinutes)} min)` : ""}
                  </span>
                  <span className="text-muted" style={{ fontSize: 9 }}>
                    {drained
                      ? `Just read ${drained.checked}: ${drained.ok} delivered, ${drained.failed} failed`
                      : health?.hasAccessToken ? "Expo access token set" : "Expo access token not set"}
                  </span>
                </div>
              </div>
            </div>

            {findings.length === 0 ? (
              <div className="nc-readiness-callout nc-readiness-callout--success">
                <i className="fas fa-check-circle" />
                <div>
                  <strong>Nothing to flag from the server side</strong>
                  <span>
                    If one phone still gets nothing, it is that phone: open the Lusuku app on it,
                    go to Account, and tap Test notification. That registers the phone if it is
                    not in the {reachable} above and reports Expo's answer for it.
                  </span>
                </div>
              </div>
            ) : (
              findings.map((f) => (
                <div key={f.title} className={`nc-readiness-callout nc-readiness-callout--${SEVERITY_CALLOUT[f.severity] || "info"}`}>
                  <i className={SEVERITY_ICON[f.severity] || SEVERITY_ICON.info} />
                  <div>
                    <strong>{f.title}</strong>
                    <span>{f.detail}</span>
                  </div>
                </div>
              ))
            )}

            {errorCodes.length > 0 && (
              <Table responsive size="sm" className="mt-2 mb-0">
                <thead>
                  <tr>
                    <th>Last error per account</th>
                    <th>Accounts</th>
                    <th>Last seen</th>
                    <th>What it means</th>
                  </tr>
                </thead>
                <tbody>
                  {errorCodes.map((e) => (
                    <tr key={e.code}>
                      <td><code>{e.code}</code></td>
                      <td>{e.count}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{e.lastAt ? formatLocalDateTime(e.lastAt) : ""}</td>
                      <td className="text-muted" style={{ fontSize: 11 }}>{e.meaning} {e.action}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

// ── Tab 2: Send push ────────────────────────────────────────────────────────────
// Deals, offers and announcements for the mobile apps, plus the operator Web
// Push broadcast. Audience, market and deep link are explicit choices: the tab
// used to send title + message only, so every send silently became "everyone,
// every market, opens nothing". The choices, the request body and the way the
// result is read live in utilities/pushBroadcast.js so they cannot drift apart.
function SendPushTab() {
  const [form, setForm] = useState({
    title: "",
    message: "",
    audience: "customers",
    userId: "",
    // Defaults to the market being viewed, not every market: a Kampala offer
    // has no business waking a Lusaka handset unless someone asks for it.
    country: getActiveCountry(),
    screen: "",
    productId: "",
    source: PUSH_PRODUCT_SOURCES[0].value,
    serviceMessage: false,
    mode: "now",
    // The operator's own clock. Converted to UTC in buildPushBroadcastBody.
    sendAtLocal: "",
  });
  const [target, setTarget] = useState({ mobile: true, operators: false });
  const [sending, setSending] = useState(false);
  // The last send, kept on screen: a toast is gone before anyone can read the
  // counts, and the counts are the whole point.
  const [report, setReport] = useState(null);

  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);
  const confirmDialog = useConfirmDialog();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setValue = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const marketOptions = pushMarketOptions();
  // The field cannot offer a time the server would refuse.
  const earliestSendAt = toLocalDateTimeInput(new Date());

  // Switching to "Schedule" with nothing picked starts an hour out, which is
  // both valid and a realistic first guess.
  const onModeChange = (e) => {
    const mode = e.target.value;
    setForm((f) => ({
      ...f,
      mode,
      sendAtLocal:
        mode === "scheduled" && !f.sendAtLocal
          ? toLocalDateTimeInput(new Date(Date.now() + 60 * 60 * 1000))
          : f.sendAtLocal,
    }));
  };

  const loadCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    try {
      const { data } = await apiListPushCampaigns({ limit: 25 });
      setCampaigns(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not load the campaign list.");
    }
    setLoadingCampaigns(false);
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const cancelCampaign = async (campaign) => {
    setCancellingId(campaign._id);
    try {
      const { data } = await apiCancelPushCampaign(campaign._id);
      if (data?.success) toast.success(`"${campaign.title}" was cancelled.`);
      else toast.error(data?.error || "That campaign could not be cancelled.");
    } catch (err) {
      toast.error(cancelErrorText(err));
    }
    setCancellingId(null);
    // Either way the row is re-read, so the list shows what actually happened.
    await loadCampaigns();
  };

  const askCancel = (campaign) => {
    confirmDialog.ask({
      title: "Cancel this scheduled push?",
      body: `"${campaign.title}" will not be sent. You can schedule it again afterwards.`,
      tone: "danger",
      confirmLabel: "Cancel the send",
      cancelLabel: "Keep it scheduled",
      busyLabel: "Cancelling...",
      onConfirm: () => cancelCampaign(campaign),
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!target.mobile && !target.operators) return toast.error("Pick at least one destination.");
    const problem = validatePushBroadcast(form, { mobile: target.mobile });
    if (problem) return toast.error(problem);

    setSending(true);
    setReport(null);
    const channels = [];
    if (target.mobile) {
      channels.push({
        key: "mobile",
        label: "Mobile apps",
        call: apiSendExpoBroadcast(buildPushBroadcastBody(form)),
      });
    }
    if (target.operators) {
      channels.push({
        key: "operators",
        label: "Dashboard operators",
        call: apiBroadcastWebPush({ title: form.title.trim(), message: form.message.trim() }),
      });
    }

    // Every outcome is read. A rejected call reports its own reason and a
    // fulfilled one still has to prove it reached a device: the API answers
    // success:false with totalSent:0 when nobody was reachable, and the old
    // code counted that as a send.
    const settled = await Promise.allSettled(channels.map((c) => c.call));
    const lines = settled.map((outcome, i) => {
      const channel = channels[i];
      if (outcome.status === "rejected") {
        return { ok: false, text: `${channel.label}: ${sendErrorText(outcome.reason)}` };
      }
      // The mobile channel has three possible answers (scheduled, sent with
      // counts, and a 202 for a send still running), so it is handed the whole
      // response, status included, not just the body.
      const summary = channel.key === "mobile"
        ? summarisePushSend(outcome.value)
        : summariseWebPushBroadcast(outcome.value?.data);
      return {
        ok: summary.ok,
        pending: Boolean(summary.pending),
        scheduled: Boolean(summary.scheduled),
        text: `${channel.label}: ${summary.text}`,
      };
    });

    const ok = lines.length > 0 && lines.every((l) => l.ok);
    setReport({
      ok,
      lines,
      pending: lines.some((l) => l.pending),
      scheduled: lines.some((l) => l.scheduled),
    });
    if (ok) {
      toast.success(lines.map((l) => l.text).join(" "));
      setForm((f) => ({ ...f, title: "", message: "", userId: "", productId: "" }));
      // Whatever just happened now has a row, including a send still running.
      loadCampaigns();
    } else {
      toast.error(lines.filter((l) => !l.ok).map((l) => l.text).join(" ") || "Send failed.");
    }
    setSending(false);
  };

  const linksToProduct = form.screen === PRODUCT_DEEP_LINK;
  const isScheduled = form.mode === "scheduled";
  const reportHeadline = !report?.ok
    ? "Not delivered"
    : report.scheduled
      ? "Scheduled"
      : report.pending
        ? "Sending"
        : "Sent";
  const reportIcon = !report?.ok
    ? "fas fa-exclamation-triangle"
    : report.scheduled
      ? "fas fa-clock"
      : report.pending
        ? "fas fa-paper-plane"
        : "fas fa-check-circle";

  return (
    <>
    <PushHealthCard onReceiptsChecked={loadCampaigns} />

    <Card className="mt-3">
      <Card.Body>
        <h5 className="mb-3"><i className="fas fa-paper-plane" />Send a push notification</h5>
        <p className="nc-card-sub mb-3">
          Deals, offers and announcements for the Lusuku mobile apps. Only a device that
          registered a push token and is signed in can receive one.
        </p>
        <Form onSubmit={submit} autoComplete="off">
          <div className="nc-section-label">Destination</div>
          <Form.Group className="mb-3">
            <Form.Check inline type="checkbox" id="send-mobile" label="Mobile apps"
              checked={target.mobile} onChange={(e) => setTarget((t) => ({ ...t, mobile: e.target.checked }))} />
            <Form.Check inline type="checkbox" id="send-operators" label="Dashboard operators (this market)"
              checked={target.operators} onChange={(e) => setTarget((t) => ({ ...t, operators: e.target.checked }))} />
          </Form.Group>

          {target.mobile && (
            <>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Mobile audience</Form.Label>
                    <AppSelect block value={form.audience} onChange={set("audience")}>
                      {PUSH_AUDIENCES.map((a) => (
                        <option key={a.value} value={a.value}>{a.label}</option>
                      ))}
                    </AppSelect>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Market</Form.Label>
                    <AppSelect block value={form.country} onChange={set("country")}>
                      {marketOptions.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </AppSelect>
                  </Form.Group>
                </Col>
              </Row>
              {form.audience === "user" && (
                <Form.Group className="mb-3">
                  <Form.Label>Customer user ID</Form.Label>
                  <Form.Control placeholder="Mongo _id of the customer" value={form.userId}
                    onChange={set("userId")} />
                </Form.Group>
              )}
            </>
          )}

          <div className="nc-section-label">Message</div>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control placeholder="Enter title..." maxLength={65} value={form.title} onChange={set("title")} />
            <small className="text-muted">{form.title.length}/65. Long titles are truncated on the lock screen.</small>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Message</Form.Label>
            <Form.Control as="textarea" rows={3} placeholder="Enter message..." maxLength={178}
              value={form.message} onChange={set("message")} />
            <small className="text-muted">{form.message.length}/178</small>
          </Form.Group>

          {target.mobile && (
            <>
              <div className="nc-section-label">When it is tapped</div>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Opens</Form.Label>
                    <AppSelect block value={form.screen} onChange={set("screen")}>
                      {PUSH_DEEP_LINKS.map((d) => (
                        <option key={d.value} value={d.value}>{d.label}</option>
                      ))}
                    </AppSelect>
                  </Form.Group>
                </Col>
                {linksToProduct && (
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Product type</Form.Label>
                      <AppSelect block value={form.source} onChange={set("source")}>
                        {PUSH_PRODUCT_SOURCES.map((p) => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </AppSelect>
                    </Form.Group>
                  </Col>
                )}
              </Row>
              {linksToProduct && (
                <Form.Group className="mb-3">
                  <Form.Label>Product ID</Form.Label>
                  <Form.Control placeholder="Mongo _id of the product" value={form.productId}
                    onChange={set("productId")} />
                </Form.Group>
              )}

              <SettingRow
                label="Service message"
                topic="pushServiceMessage"
                sub="Also reach customers who turned Offers off"
              >
                <Toggle id="send-service-message" checked={form.serviceMessage}
                  onChange={(v) => setValue("serviceMessage", v)} />
              </SettingRow>

              <div className="nc-section-label">Timing</div>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>When</Form.Label>
                    <AppSelect block value={form.mode} onChange={onModeChange}>
                      {PUSH_TIMING.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </AppSelect>
                  </Form.Group>
                </Col>
                {isScheduled && (
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date and time</Form.Label>
                      <Form.Control type="datetime-local" value={form.sendAtLocal}
                        min={earliestSendAt} onChange={set("sendAtLocal")} />
                      <small className="text-muted">
                        Your local time ({localTimeZoneLabel()}). It goes out then whether
                        or not anyone is at the dashboard.
                      </small>
                    </Form.Group>
                  </Col>
                )}
              </Row>
            </>
          )}

          <Button type="submit" variant="info" className="mt-3" disabled={sending}>
            {sending
              ? <Spinner size="sm" animation="border" />
              : isScheduled ? "Schedule Push Notification" : "Send Push Notification"}
          </Button>
        </Form>

        {report && (
          <div className={`nc-readiness-callout mt-3 ${report.ok ? "nc-readiness-callout--success" : "nc-readiness-callout--danger"}`}>
            <i className={reportIcon} />
            <div>
              <strong>{reportHeadline}</strong>
              {report.lines.map((l) => (
                <span key={l.text}>{l.text}</span>
              ))}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>

    <Card className="mt-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0"><i className="fas fa-list-ul" />Scheduled and recent</h5>
          <Button variant="outline-secondary" size="sm" onClick={loadCampaigns} disabled={loadingCampaigns}>
            {loadingCampaigns ? <Spinner size="sm" animation="border" /> : "Refresh"}
          </Button>
        </div>
        {loadingCampaigns && campaigns.length === 0 ? (
          <Loader label="Loading campaigns..." />
        ) : campaigns.length === 0 ? (
          <div className="text-muted">Nothing has been sent or scheduled yet.</div>
        ) : (
          <Table responsive hover size="sm">
            <thead>
              <tr>
                <th>Title</th>
                <th>Audience</th>
                <th>Market</th>
                <th>When</th>
                <th>Status</th>
                <th>Sent / targeted</th>
                <th>Delivered</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => {
                const when = campaignWhen(c);
                const status = pushCampaignStatus(c.status);
                const sender = campaignSender(c);
                const delivery = campaignDelivery(c);
                return (
                  <tr key={c._id}>
                    <td>
                      {c.title}
                      {sender && (
                        <div className="text-muted" style={{ fontSize: 11 }}>by {sender}</div>
                      )}
                      {c.failureReason && (
                        <div className="text-danger" style={{ fontSize: 11 }}>{c.failureReason}</div>
                      )}
                    </td>
                    <td>{campaignAudienceLabel(c)}</td>
                    <td>{campaignMarketLabel(c)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{when.label} {when.when}</td>
                    <td><Badge bg={status.variant}>{status.label}</Badge></td>
                    <td>{campaignCounts(c).text}</td>
                    <td>
                      {delivery.text && (
                        <span className={`nc-state nc-state--${delivery.tone}`}>{delivery.text}</span>
                      )}
                      {delivery.codes && (
                        <div className="text-muted" style={{ fontSize: 11 }}>{delivery.codes}</div>
                      )}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {isCancellableCampaign(c) && (
                        <Button size="sm" variant="outline-danger"
                          disabled={cancellingId === c._id}
                          onClick={() => askCancel(c)}>
                          {cancellingId === c._id
                            ? <Spinner size="sm" animation="border" />
                            : "Cancel"}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>

    <ConfirmDialog {...confirmDialog.props} />
    </>
  );
}

// ── Tab 3: Alert settings (staff) ────────────────────────────────────────────────
/* The team settings change every dashboard in the market, so saving them
   needs the settings update level (the server's requireSettingsAccess). A
   person's own preferences on My alerts stay open to every staff member. */
function AlertSettingsTab({ canEdit = false }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const previewRef = useRef(null);
  const previewTimer = useRef(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    apiGetNotificationSettings()
      .then(({ data }) => setSettings(data.settings))
      .catch((e) => toast.error(e?.response?.data?.error || "Failed to load settings"))
      .finally(() => setLoading(false));
  }, []);

  const patch = (path, value) => {
    setSettings((s) => {
      const next = JSON.parse(JSON.stringify(s));
      let node = next;
      const parts = path.split(".");
      for (let i = 0; i < parts.length - 1; i += 1) node = node[parts[i]] = node[parts[i]] || {};
      node[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { sound, webPush, desktop, events, quietHours } = settings;
      const { data } = await apiUpdateNotificationSettings({ sound, webPush, desktop, events, quietHours });
      setSettings(data.settings);
      announceOrderAlertsConfigChanged();
      toast.success("Settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Save failed.");
    }
    setSaving(false);
  };

  const selectedToneUrl = () => resolveToneUrl(settings?.sound?.tone, settings?.sound?.customTone?.url, notificationSound);
  const previewTone = (url, v) => {
    try {
      if (!previewRef.current) previewRef.current = new Audio();
      const a = previewRef.current;
      a.src = url;
      a.volume = Math.max(0, Math.min(1, typeof v === "number" ? v : (settings?.sound?.volume ?? 0.6)));
      a.currentTime = 0;
      a.play().catch(() => {});
    } catch (_e) { /* ignore */ }
  };
  // Sample the SELECTED tone shortly after the last drag tick so admins hear the level.
  const onVolumeChange = (v) => {
    patch("sound.volume", v);
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => previewTone(selectedToneUrl(), v), 200);
  };
  const onUploadTone = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("audio/")) { toast.error("Please choose an audio file."); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error("Tone is too large (max ~3MB)."); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setUploading(true);
      try {
        const { data } = await apiUploadTone(reader.result, file.name);
        if (data?.url) {
          patch("sound.customTone", { url: data.url, name: file.name });
          patch("sound.tone", "custom");
          toast.success("Custom tone uploaded. Press Save settings to use it for the team.");
        } else {
          toast.error(data?.error || "Upload failed.");
        }
      } catch (err) {
        toast.error(err?.response?.data?.error || "Upload failed.");
      }
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  if (loading) return <Loader label="Loading settings…" />;
  if (!settings) return <div className="alert alert-danger mt-3">Couldn't load settings.</div>;

  const vol = settings.sound?.volume ?? 0.6;

  // `channels` lists the channels actually wired for this event. A channel the
  // event cannot fire shows a dash instead of a switch — a toggle that changes
  // nothing is worse than no toggle (see the alert-channels audit).
  const evtRow = (key, label, channels = ["sound", "webPush", "desktop"]) => {
    const ev = settings.events?.[key] || {};
    return (
      <tr key={key}>
        <td>{label}</td>
        {["sound", "webPush", "desktop"].map((ch) => (
          <td key={ch} className="text-center">
            {channels.includes(ch) ? (
              <Toggle id={`ev-${key}-${ch}`} checked={ev[ch] !== false}
                onChange={(v) => patch(`events.${key}.${ch}`, v)} />
            ) : (
              <span
                className="text-muted"
                title="Not available for this event"
                aria-label="Not available for this event"
              >
                &mdash;
              </span>
            )}
          </td>
        ))}
      </tr>
    );
  };

  return (
    <Card className="mt-3">
      <Card.Body>
        <h5 className="mb-2"><i className="fas fa-sliders-h" />Team alert settings for this market<SettingHelp topic="operatorAlertSettings" /></h5>
        <p className="nc-card-sub" style={{ marginBottom: 20 }}>
          These apply to every dashboard in this market, vendor dashboards included, unless a person saved their own under My alerts. Switches marked as not applied yet are saved but nothing reads them; hover the info icons for what each one really does.
        </p>

        <Row>
          <Col md={6}>
            <div className="nc-section-label">Channels</div>
            <SettingRow label="Browser push" topic="operatorAlertWebPush" sub="New orders and new sellers, even with the dashboard closed.">
              <Toggle id="webpush-enabled" checked={settings.webPush?.enabled !== false}
                onChange={(v) => patch("webPush.enabled", v)} />
            </SettingRow>
            <SettingRow label="Desktop pop up" topic="operatorAlertDesktop" sub="Applied to the new seller pop up only today.">
              <Toggle id="desktop-enabled" checked={settings.desktop?.enabled !== false}
                onChange={(v) => patch("desktop.enabled", v)} />
            </SettingRow>
            <SettingRow label="In-tab alert sound" topic="operatorAlertSound" sub="Saved, but not applied yet.">
              <Toggle id="sound-enabled" checked={settings.sound?.enabled !== false}
                onChange={(v) => patch("sound.enabled", v)} />
            </SettingRow>

            <div className="nc-volume">
              <div className="nc-volume-head">
                <span className="nc-setting-label">Alert volume: {Math.round(vol * 100)}%
                  <SettingHelp topic="operatorAlertVolume" />
                </span>
                <Button size="sm" variant="outline-secondary" onClick={() => previewTone(selectedToneUrl(), vol)}>
                  <i className="fas fa-volume-up" />Preview
                </Button>
              </div>
              <Form.Range min={0} max={1} step={0.05} value={vol}
                onChange={(e) => onVolumeChange(parseFloat(e.target.value))} />
            </div>

            <SettingRow label="Repeat the new order sound" topic="operatorAlertRepeat" sub="Plays again on a timer until the play limit is reached.">
              <Toggle id="repeat-until-ack" checked={settings.sound?.repeatUntilAck === true}
                onChange={(v) => patch("sound.repeatUntilAck", v)} />
            </SettingRow>
            {settings.sound?.repeatUntilAck && (
              <Row className="mt-2">
                <Col xs={6}>
                  <Form.Label style={{ fontSize: 13 }}>Seconds between plays<SettingHelp topic="operatorAlertRepeatInterval" /></Form.Label>
                  <Form.Control type="number" min={2} value={Math.round((settings.sound?.repeatIntervalMs ?? 8000) / 1000)}
                    onChange={(e) => patch("sound.repeatIntervalMs", Math.max(2, +e.target.value) * 1000)} />
                </Col>
                <Col xs={6}>
                  <Form.Label style={{ fontSize: 13 }}>Plays in total<SettingHelp topic="operatorAlertMaxPlays" /></Form.Label>
                  <Form.Control type="number" min={1} value={settings.sound?.maxRepeats ?? 8}
                    onChange={(e) => patch("sound.maxRepeats", Math.max(1, +e.target.value))} />
                </Col>
              </Row>
            )}

            <div className="nc-section-label" style={{ marginTop: 18 }}>
              New order tone
              <SettingHelp topic="operatorAlertTone" />
            </div>
            <div className="nc-tones">
              {TONES.map((t) => (
                <label key={t.id} className={`nc-tone${settings.sound?.tone === t.id ? " sel" : ""}`}>
                  <input type="radio" name="tone" checked={settings.sound?.tone === t.id}
                    onChange={() => patch("sound.tone", t.id)} />
                  <span className="nc-tone-name">{t.name}</span>
                  <button type="button" className="nc-tone-play" aria-label={`Preview ${t.name}`}
                    onClick={(e) => { e.preventDefault(); previewTone(t.url, vol); }}>
                    <i className="fas fa-play" />
                  </button>
                </label>
              ))}
              <label className={`nc-tone${settings.sound?.tone === "custom" ? " sel" : ""}${settings.sound?.customTone?.url ? "" : " disabled"}`}>
                <input type="radio" name="tone" checked={settings.sound?.tone === "custom"}
                  disabled={!settings.sound?.customTone?.url}
                  onChange={() => settings.sound?.customTone?.url && patch("sound.tone", "custom")} />
                <span className="nc-tone-name">{settings.sound?.customTone?.name || "Custom"}</span>
                {settings.sound?.customTone?.url && (
                  <button type="button" className="nc-tone-play" aria-label="Preview custom tone"
                    onClick={(e) => { e.preventDefault(); previewTone(settings.sound.customTone.url, vol); }}>
                    <i className="fas fa-play" />
                  </button>
                )}
              </label>
            </div>
            <div className="nc-tone-upload">
              <input ref={fileRef} type="file" accept="audio/*" style={{ display: "none" }} onChange={onUploadTone} />
              <Button size="sm" variant="outline-secondary" disabled={uploading} onClick={() => fileRef.current && fileRef.current.click()}>
                {uploading ? <Spinner size="sm" animation="border" /> : <><i className="fas fa-upload" />Upload your own</>}
              </Button>
              <span className="nc-tone-hint">MP3 or WAV, up to ~3MB.</span>
            </div>
          </Col>

          <Col md={6}>
            <div className="nc-section-label">Quiet hours</div>
            <SettingRow label="Use quiet hours" topic="operatorAlertQuietHours" sub="Saved, but not applied yet.">
              <Toggle id="quiet-enabled" checked={settings.quietHours?.enabled === true}
                onChange={(v) => patch("quietHours.enabled", v)} />
            </SettingRow>
            {settings.quietHours?.enabled && (
              <>
                <Row className="mt-1">
                  <Col xs={6}>
                    <Form.Label style={{ fontSize: 13 }}>From</Form.Label>
                    <Form.Control type="time" value={settings.quietHours?.start ?? "22:00"}
                      onChange={(e) => patch("quietHours.start", e.target.value)} />
                  </Col>
                  <Col xs={6}>
                    <Form.Label style={{ fontSize: 13 }}>To</Form.Label>
                    <Form.Control type="time" value={settings.quietHours?.end ?? "06:00"}
                      onChange={(e) => patch("quietHours.end", e.target.value)} />
                  </Col>
                </Row>
                <SettingRow label="Mute sound only" topic="operatorAlertMuteSoundOnly" sub="Saved, but not applied yet.">
                  <Toggle id="quiet-mute-sound-only" checked={settings.quietHours?.muteSoundOnly !== false}
                    onChange={(v) => patch("quietHours.muteSoundOnly", v)} />
                </SettingRow>
              </>
            )}

            <div className="nc-section-label" style={{ marginTop: 24 }}>
              Which events use which channel
              <SettingHelp topic="operatorAlertEventChannels" />
            </div>
            <Table size="sm" bordered className="mt-2 nc-evt-table">
              <thead>
                <tr><th>Event</th><th className="text-center">Sound</th><th className="text-center">Browser push</th><th className="text-center">Desktop pop up</th></tr>
              </thead>
              <tbody>
                {evtRow("newOrder", "New order")}
                {evtRow("orderCancelled", "Order cancelled (no alert sent yet)")}
                {/* No sound: the alert tone stays reserved for orders, so a
                    seller sign-up never competes with a live order beep. */}
                {evtRow("newSeller", "New seller awaiting verification", ["webPush", "desktop"])}
              </tbody>
            </Table>
          </Col>
        </Row>

        <div className="mt-4">
          {canEdit ? (
            <Button variant="primary" onClick={save} disabled={saving}>
              {saving ? <Spinner size="sm" animation="border" /> : "Save settings"}
            </Button>
          ) : (
            <ReadOnlyNotice level="update" />
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

// ── Tab 4: Devices (staff) ───────────────────────────────────────────────────────
function DevicesTab() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    apiListPushDevices()
      .then(({ data }) => setDevices(data.devices || []))
      .catch((e) => toast.error(e?.response?.data?.error || "Failed to load devices"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader label="Loading devices…" />;

  return (
    <Card className="mt-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0" style={{ display: "flex", alignItems: "center", gap: 10 }}><i className="fas fa-desktop" />Subscribed devices ({devices.length})</h5>
          <Button size="sm" variant="outline-secondary" onClick={load}><i className="fas fa-sync" />Refresh</Button>
        </div>
        {devices.length === 0 ? (
          <div className="text-muted">No devices are subscribed for this market yet.</div>
        ) : (
          <Table responsive hover size="sm">
            <thead>
              <tr><th>Device</th><th>Role</th><th>Country</th><th>Enabled</th><th>Last seen</th></tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <tr key={d._id}>
                  <td>{d.label || "Browser"}</td>
                  <td>{d.isStaff ? <Badge bg="info">staff</Badge> : <Badge bg="secondary">{d.operationMode || "vendor"}</Badge>}</td>
                  <td>{d.country}</td>
                  <td>{d.enabled ? <Badge bg="success">yes</Badge> : <Badge bg="secondary">no</Badge>}</td>
                  <td>{d.lastSeenAt ? new Date(d.lastSeenAt).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}

// ── Tab 5: History (staff) ───────────────────────────────────────────────────────
function HistoryTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGetNotificationHistory({ limit: 50 })
      .then(({ data }) => setItems(data.items || []))
      .catch((e) => toast.error(e?.response?.data?.error || "Failed to load history"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader label="Loading history…" />;

  const typeBadge = (t) => {
    const map = { new_order: "success", broadcast: "info", test: "secondary", order_status: "warning" };
    return <Badge bg={map[t] || "secondary"}>{t}</Badge>;
  };

  return (
    <Card className="mt-3">
      <Card.Body>
        <h5 className="mb-3"><i className="fas fa-history" />Notification history</h5>
        {items.length === 0 ? (
          <div className="text-muted">No notifications logged yet for this market.</div>
        ) : (
          <Table responsive hover size="sm">
            <thead>
              <tr><th>When</th><th>Type</th><th>Title</th><th>Delivered</th><th>Failed</th></tr>
            </thead>
            <tbody>
              {items.map((n) => (
                <tr key={n._id}>
                  <td style={{ whiteSpace: "nowrap" }}>{new Date(n.createdAt).toLocaleString()}</td>
                  <td>{typeBadge(n.type)}</td>
                  <td>{n.title}</td>
                  <td>{n.delivered ?? 0}/{n.recipients ?? 0}</td>
                  <td>{n.failed ? <span className="text-danger">{n.failed}</span> : 0}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}

// ── Shell ────────────────────────────────────────────────────────────────────────
const NotificationsCenter = ({ canEdit = false }) => {
  const isStaff = useIsStaff();
  const [key, setKey] = useState("device");

  return (
    <div className="notif-center">
      <Container fluid>
      <Row>
        <Col xs="12">
          <div className="nc-header">
            <span className="nc-header-icon"><i className="fas fa-bell" /></span>
            <div>
              <span className="nc-header-eyebrow">Communications</span>
              <h4 className="nc-header-title">Notifications</h4>
              <p className="nc-header-sub">Control order alerts, subscribe this browser, and review what's been sent.</p>
            </div>
          </div>
          <Tabs id="notif-tabs" activeKey={key} onSelect={(k) => setKey(k)} className="mb-1">
            <Tab eventKey="device" title={<><i className="fas fa-bell" />This device</>}>
              <ThisDeviceTab />
            </Tab>
            <Tab eventKey="myalerts" title={<><i className="fas fa-user-cog" />My alerts</>}>
              <MyAlertsTab />
            </Tab>
            {isStaff && (
              <Tab eventKey="send" title={<><i className="fas fa-paper-plane" />Send push</>}>
                <SendPushTab />
              </Tab>
            )}
            {isStaff && (
              <Tab eventKey="settings" title={<><i className="fas fa-sliders-h" />Alert settings</>}>
                <AlertSettingsTab canEdit={canEdit} />
              </Tab>
            )}
            {isStaff && (
              <Tab eventKey="devices" title={<><i className="fas fa-desktop" />Devices</>}>
                <DevicesTab />
              </Tab>
            )}
            {isStaff && (
              <Tab eventKey="history" title={<><i className="fas fa-history" />History</>}>
                <HistoryTab />
              </Tab>
            )}
          </Tabs>
        </Col>
      </Row>
      </Container>
    </div>
  );
};

export default NotificationsCenter;
